import { normalizePrintingLabel } from './variantLabeling'
import { CONDITION_RANK_BY_LABEL, STANDARD_CONDITIONS } from './conditions'

export { UUID_PATTERN } from './uuidPattern'

/** Standard condition labels for dropdowns (trade builder, inventory-style pickers). */
export { STANDARD_CONDITIONS }

/** Condition rank by exact label; the canonical table in ./conditions owns it. */
export const CONDITION_ORDER: Readonly<Record<string, number>> = CONDITION_RANK_BY_LABEL

export const PRINTING_ORDER: Record<string, number> = {
  'Normal': 1,
  'Non-Holo': 1,
  'Holo': 2,
  'Holofoil': 2,
  'Reverse Holo': 3,
  'Reverse Holofoil': 3,
  'Mirror Holofoil': 4,
  'Full Art': 5,
  'Alternate Art': 6,
  'Borderless': 7,
  'Extended Art': 8,
  'Retro Frame': 9,
  'Textured Foil': 10,
  'Etched Foil': 11,
  'Illustration Rare': 12,
  'Special Illustration Rare': 13,
  'Hyper Rare': 14,
  'Secret Rare': 15,
  'Showcase': 16,
  'Parallel': 17,
  'Leader Parallel': 18,
  'Manga': 19,
  'Manga Alternate Art': 20,
  'Master Ball': 21,
  'Poke Ball': 22,
  'Shadowless Holofoil': 23,
  'Shadowless': 24,
  '1st Edition Holofoil': 25,
  '1st Edition': 26,
  'First Edition': 26,
  'Unlimited Holofoil': 27,
  'Unlimited': 28,
  'Staff': 29,
  'Prerelease': 30,
  'Stamped': 31,
  'Sealed': 32,
}

export const normalizeConditionForOrder = (condition?: string | null): string => String(condition || '').trim()
export const normalizePrintingForOrder = (printing?: string | null): string => normalizePrintingLabel(printing)

/** Sort condition labels for stable pickers (inventory filters, catalog-style lists). */
export const sortConditions = (conditions: string[]): string[] => {
  return [...conditions].sort((a, b) => {
    const aKey = normalizeConditionForOrder(a)
    const bKey = normalizeConditionForOrder(b)
    const aOrder = CONDITION_ORDER[aKey] ?? 999
    const bOrder = CONDITION_ORDER[bKey] ?? 999
    if (aOrder !== bOrder) return aOrder - bOrder
    return aKey.localeCompare(bKey, undefined, { sensitivity: 'base' })
  })
}

/** Sort printing labels for stable pickers (inventory filters, catalog-style lists). */
export const sortPrintings = (printings: string[]): string[] => {
  return [...printings].sort((a, b) => {
    const aKey = normalizePrintingForOrder(a)
    const bKey = normalizePrintingForOrder(b)
    const aOrder = PRINTING_ORDER[aKey] ?? 999
    const bOrder = PRINTING_ORDER[bKey] ?? 999
    if (aOrder !== bOrder) return aOrder - bOrder
    return aKey.localeCompare(bKey, undefined, { sensitivity: 'base' })
  })
}

export const compareVariantOrder = (
  a: { printing?: string | null; condition?: string | null },
  b: { printing?: string | null; condition?: string | null }
): number => {
  const printingA = normalizePrintingForOrder(a.printing) || 'Normal'
  const printingB = normalizePrintingForOrder(b.printing) || 'Normal'
  const printingOrderA = PRINTING_ORDER[printingA] ?? 999
  const printingOrderB = PRINTING_ORDER[printingB] ?? 999

  if (printingOrderA !== printingOrderB) {
    return printingOrderA - printingOrderB
  }

  const printingLabelCompare = printingA.localeCompare(printingB, undefined, { sensitivity: 'base' })
  if (printingLabelCompare !== 0) {
    return printingLabelCompare
  }

  const conditionA = normalizeConditionForOrder(a.condition) || 'Near Mint'
  const conditionB = normalizeConditionForOrder(b.condition) || 'Near Mint'
  const conditionOrderA = CONDITION_ORDER[conditionA] ?? 999
  const conditionOrderB = CONDITION_ORDER[conditionB] ?? 999

  if (conditionOrderA !== conditionOrderB) {
    return conditionOrderA - conditionOrderB
  }

  return conditionA.localeCompare(conditionB, undefined, { sensitivity: 'base' })
}
